// src/cases/FormSimpleShow.js
// Detail view for a single simplified-form lead.
import React from "react";
import { Show, useRecordContext } from "react-admin";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Chip,
  Divider,
} from "@mui/material";
import { formatDateForDisplay } from "../utils/dateUtils";
import ApiSendButtons from "../components/ApiSendButtons";

const Row = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" sx={{ color: "var(--ed-text-3, #6c757d)", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </Typography>
    <Typography variant="body1" sx={{ fontWeight: 500, color: "var(--ed-text, #212529)" }}>
      {value === 0 || value ? value : "-"}
    </Typography>
  </Grid>
);

const formatMoney = (value) =>
  typeof value === "number"
    ? value.toLocaleString("es-ES", { minimumFractionDigits: 0 }) + " €"
    : value || "-";

const FormSimpleShowLayout = () => {
  const record = useRecordContext();
  if (!record) return null;

  const titular = record.primerTitular || {};
  const nombre = titular.nombre || record.nombre || "";
  const apellidos = titular.apellidos || record.apellidos || "";

  return (
    <Box sx={{ maxWidth: 900, margin: "0 auto" }}>
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {`${nombre} ${apellidos}`.trim() || "Lead"}
            </Typography>
            <Chip
              label={record.status || "FINANCIACIÓN PRE ACEPTADA"}
              color="success"
              sx={{ fontWeight: 600 }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: "var(--ed-text-3, #6c757d)", mt: 0.5 }}>
            Registrado: {formatDateForDisplay(record.registeredDate)}
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Integraciones API
          </Typography>
          <ApiSendButtons
            collection="formSimpleLeads"
            caseId={record.id}
            providers={["deudafix", "unifye"]}
            initialSends={record.apiSends}
          />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Datos de contacto
          </Typography>
          <Grid container spacing={2}>
            <Row label="Nombre" value={nombre} />
            <Row label="Apellidos" value={apellidos} />
            <Row label="Fecha de nacimiento" value={formatDateForDisplay(record.fechaNacimiento)} />
            <Row label="Teléfono" value={titular.telefono || record.telefono} />
            <Row label="Correo" value={titular.correo || record.correo} />
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Solicitud
          </Typography>
          <Grid container spacing={2}>
            <Row label="Importe solicitado" value={formatMoney(record.importeSolicitado)} />
            <Row label="Motivo del préstamo" value={record.motivoPrestamo} />
            <Row label="Ingreso mensual" value={formatMoney(record.ingresoMensual)} />
            <Row label="Cuota mensual de créditos" value={formatMoney(record.cuotaMensualCreditos)} />
            <Row label="Antigüedad laboral" value={formatDateForDisplay(record.antiguedadLaboral)} />
            <Row label="Tipo de contrato" value={record.tipoContrato} />
            <Row label="Tipo de vivienda" value={record.tipoVivienda} />
            <Row label="Situación familiar" value={record.situacionFamiliar} />
            <Row label="¿Impago o ASNEF?" value={record.impagoAsnef} />
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Row
              label="Acepta tratamiento de datos"
              value={record.aceptaTratamiento ? "Sí" : "No"}
            />
            <Row label="Origen" value={record.source} />
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Gestión
          </Typography>
          <Grid container spacing={2}>
            <Row label="Operaciones" value={record.operacionesAgent || "Por Atender"} />
            <Row label="Comercial" value={record.comercialAgent || "No Asignado"} />
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export const FormSimpleShow = () => (
  <Show>
    <FormSimpleShowLayout />
  </Show>
);
